<template>
  <transition name="fade">
    <Loader v-if="loading" />
    <div class="home flex" v-else>
      <side-menu />
      <div class="lg:pl-16 flex-grow w-full min-h-screen">
        <NavBar title="Settings" />
        <div class="flex flex-col w-full p-8 space-y-4 md:w-8/12 lg:w-8/12 xl:w-6/12">
          <div
            class="flex flex-col w-full h-full  items-stretch justify-between bg-white dark:bg-bs-dark-bg-over rounded-3xl tip-item p-3 xl:p-5"
          >
            <label
              class="text-base text-left block text-bs-gray-dark dark:text-white font-medium"
            >
              Currency
            </label>
            <select
              v-model="currencyCode"
              class="py-3 px-4 border-bs-gray-light border bg-white dark:bg-bs-dark-bg dark:text-white dark:border-bs-blue-dark rounded-xl"
            >
              <option :value="currency" v-for="currency in currencies" :key="currency">
                {{ currency }}
              </option>
            </select>
          </div>
          <div
            class="flex flex-col w-full h-full  items-stretch justify-between bg-white dark:bg-bs-dark-bg-over rounded-3xl tip-item p-3 xl:p-5"
          >
            <label
              class="text-base text-left block text-bs-gray-dark dark:text-white font-medium"
            >
              Minimum Confidence
            </label>
            <div class="flex items-center">
              <button @click="minConfidenceLevel = 0" class="m-0 p-0 btn-primary text-xs mr-4">Any confidence level</button>
              <star-rating v-model:rating="minConfidenceLevel" star-size="30" inactive-color="#07255B" text-class="text-bs-gray-dark dark:text-white ml-auto font-medium"></star-rating>
            </div>
          </div>
          <div
            class="flex flex-col w-full  items-stretch justify-between bg-white dark:bg-bs-dark-bg-over rounded-3xl tip-item p-3 xl:p-5"
          >
            <label
              class="text-base text-left block text-bs-gray-dark dark:text-white font-medium"
            >
              Starting Equity
            </label>
            <div class="flex flex-wrap items-stretch w-full relative">
              <div class="flex -mr-px">
                <span
                  class="flex items-center leading-normal bg-grey-lighter rounded-xl rounded-r-none border border-r-0 border-grey-light px-3 whitespace-no-wrap text-grey-dark text-sm dark:bg-bs-dark-bg dark:text-white dark:border-bs-blue-dark"
                  >{{ currencyCode }}</span
                >
              </div>
              <input
                v-model="startingEquity"
                type="number"
                step="1"
                min="1"
                class="flex-shrink flex-grow flex-auto leading-normal w-px border py-3 border-bs-dark-gray-light rounded-xl rounded-l-none px-3 relative focus:border-blue focus:shadow dark:bg-bs-dark-bg dark:text-white dark:border-bs-blue-dark"
                placeholder="Starting Equity"
              />
            </div>
          </div>

          <div
            class="flex flex-col w-full  items-stretch justify-between bg-white dark:bg-bs-dark-bg-over rounded-3xl tip-item p-3 xl:p-5"
          >
            <label
              class="text-base text-left block text-bs-gray-dark dark:text-white font-medium"
            >
              Staking Amount (%)
            </label>

            <div class="flex flex-wrap items-stretch w-full relative">
              <input
                v-model="stakeAmount"
                type="text"
                class="flex-shrink flex-grow flex-auto leading-normal w-px border py-3 px-4 border-bs-dark-gray-light rounded-xl rounded-r-none relative focus:border-blue focus:shadow dark:bg-bs-dark-bg dark:text-white dark:border-bs-blue-dark"
                placeholder="Starting Equity"
              />
              <div class="flex -mr-px">
                <span
                  class="flex items-center leading-normal bg-grey-lighter rounded-xl rounded-l-none border border-l-0 border-grey-light px-3 whitespace-no-wrap text-grey-dark text-sm dark:bg-bs-dark-bg dark:text-white dark:border-bs-blue-dark"
                  >%</span
                >
              </div>
            </div>
          </div>
          <div class="w-full  items-stretch">
            <label
              class="mt-8 font-medium block pb-1 text-tx text-gray-400"
              v-if="setupDate.length"
              >Last changed: {{ setupDate }}</label
            >
            <button
              type="submit"
              class="btn-primary h-12 mb-4 w-full"
              @click.stop="onUpdate"
            >
              Update settings.
            </button>
           
            <span class="text-gray-400 text-left">Tips which come in after updating the settings will utilise the new starting equity.</span>
          </div>
          <transition name="fade-y"  mode="out-in">
            <div v-if="updated" class="bg-bs-green px-3 py-4 my-2 rounded-lg text-white">Settings updated</div>
          </transition>
        </div>
      </div>
      <mob-menu class="lg:hidden" />
    </div>
  </transition>
</template>

<script>
import NavBar from "@/components/organisms/NavBar.vue";
import SideMenu from "@/components/organisms/SideMenu.vue";
import Loader from "@/components/atoms/Loader.vue";
import MobMenu from "@/components/organisms/MobMenu.vue";
import StarRating from 'vue-star-rating'
import { mapActions, mapState } from "vuex";
import format from "date-fns/esm/format";
export default {
  components: {
    SideMenu,
    NavBar,
    Loader,
    MobMenu,
    StarRating
  },
  name: "Settings",
  data: function () {
    return {
      currencyCode: "EUR",
      startingEquity: 1000,
      stakeAmount: 10,
      currencies: ["EUR", "GBP"],
      loading: false,
      updated: false,
      minConfidenceLevel: 4
    };
  },
  computed: {
    ...mapState({
      storedSettings: "settings",
    }),
    setupDate: function () {
      return this.storedSettings?.setupDate
        ? format(new Date(this.storedSettings.setupDate), "dd-MM-yyyy HH:mm")
        : "";
    },
  },
  watch: {
    storedSettings: {
      immediate: true,
      deep: true,
      handler: function (curr) {
        let { currencyCode, startingEquity, stakeAmount, minConfidenceLevel } = curr;
        this.currencyCode = currencyCode;
        this.startingEquity = startingEquity;
        this.stakeAmount = stakeAmount;
        this.minConfidenceLevel = minConfidenceLevel
      },
    },
  },
  created: async function () {
    this.loading = true;
    await this.getSettings();
    this.loading = false;
  },
  methods: {
    ...mapActions({
      getSettings: "getSettings",
    }),
    onUpdate: function () {
      this.loading = true;
      let { currencyCode, startingEquity, stakeAmount,minConfidenceLevel } = this;

      this.$store
        .dispatch("updateSettings", {
          currencyCode,
          startingEquity,
          stakeAmount,
          minConfidenceLevel
        })
        .then(() => {
          this.onReset();
        })
        .catch((e) => {
          console.log(e);
        });
    },
    onReset: function () {
      this.loading = true;
      this.$store
        .dispatch("resetEquity", {})
        .then(() => {
          this.updated = true;
          setTimeout(() => {
            this.updated = false;
          }, 5000)
          this.loading = false;
        })
        .catch((e) => {
          this.loading = false;
          console.log(e);
        });
    },
  },
};
</script>

<style></style>
